import { useEffect, useRef } from "react";
import "./style.css";

/**
 * Brand palette — indigo · electric violet · primary blue.
 * Mirrors --color-accent-* / --color-primary-* from styles/variables.css.
 * (Previously an aurora green/teal set, which clashed with the navy brand.)
 */
const PALETTE = [
  "122,  89, 247", // accent-400  electric indigo
  "157, 130, 250", // accent-300  soft violet
  "196, 180, 252", // accent-200  pale violet
  "90,  131, 212", // primary-300 brand blue
  "148, 179, 232", // primary-200 pale blue
];

// Fixed ambient glow blobs
const BLOBS = [
  { rx: 0.75, ry: 0.15, r: 340, color: "91,  51, 245", a: 0.09 },
  { rx: 0.15, ry: 0.75, r: 270, color: "39,  74, 145", a: 0.07 },
  { rx: 0.90, ry: 0.80, r: 220, color: "122, 89, 247", a: 0.06 },
];

// Pointer repulsion
const POINTER_RADIUS = 130;
const POINTER_FORCE = 0.9;

export default function NeuralNetCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;

    function fit() {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }
    fit();
    window.addEventListener("resize", fit);

    const mobile = () => canvas.width < 768;

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Pointer position in canvas space; null when the cursor is away.
    const pointer = { x: null, y: null };

    function onPointerMove(e) {
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
    }

    function onPointerLeave() {
      pointer.x = null;
      pointer.y = null;
    }

    // Listen on the section, not the canvas — the canvas sits behind the
    // hero content and would never receive the events itself.
    const host = canvas.parentElement || canvas;
    if (!prefersReducedMotion && !mobile()) {
      host.addEventListener("pointermove", onPointerMove);
      host.addEventListener("pointerleave", onPointerLeave);
    }

    function mkNode() {
      const isHub = Math.random() < 0.14;
      return {
        x:     Math.random() * canvas.width,
        y:     Math.random() * canvas.height,
        vx:    (Math.random() - 0.5) * (isHub ? 0.22 : 0.44),
        vy:    (Math.random() - 0.5) * (isHub ? 0.22 : 0.44),
        r:     isHub ? 3.8 + Math.random() * 2.2 : 1.2 + Math.random() * 1.6,
        φ:     Math.random() * Math.PI * 2,
        dφ:    0.014 + Math.random() * 0.024,
        color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
        isHub,
      };
    }

    const COUNT = mobile() ? 42 : 72;
    const DIST  = mobile() ? 130 : 168;
    const nodes = Array.from({ length: COUNT }, mkNode);
    let pulses  = [];
    let tick    = 0;

    function spawnPulse() {
      for (let i = 0; i < 25; i++) {
        const a = nodes[Math.floor(Math.random() * nodes.length)];
        const b = nodes[Math.floor(Math.random() * nodes.length)];
        if (a === b) continue;
        if (Math.hypot(a.x - b.x, a.y - b.y) < DIST) {
          pulses.push({
            a, b, t: 0,
            dt:    0.008 + Math.random() * 0.013,
            color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
          });
          return;
        }
      }
    }

    function drawAmbient(W, H) {
      for (const b of BLOBS) {
        const g = ctx.createRadialGradient(b.rx * W, b.ry * H, 0, b.rx * W, b.ry * H, b.r);
        g.addColorStop(0, `rgba(${b.color}, ${b.a})`);
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(b.rx * W, b.ry * H, b.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function frame() {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      drawAmbient(W, H);

      // ── move ──────────────────────────────────────────────────────────────
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy; n.φ += n.dφ;

        // Cursor pushes nearby nodes away, easing off with distance. Applied
        // as a position offset rather than to velocity, so the field settles
        // back to its drift instead of accumulating speed.
        if (pointer.x !== null) {
          const dx = n.x - pointer.x;
          const dy = n.y - pointer.y;
          const d = Math.hypot(dx, dy);
          if (d < POINTER_RADIUS && d > 0.01) {
            const push = (1 - d / POINTER_RADIUS) ** 2 * POINTER_FORCE;
            n.x += (dx / d) * push * 6;
            n.y += (dy / d) * push * 6;
          }
        }

        if (n.x < 0) { n.x = 0; n.vx =  Math.abs(n.vx); }
        if (n.x > W) { n.x = W; n.vx = -Math.abs(n.vx); }
        if (n.y < 0) { n.y = 0; n.vy =  Math.abs(n.vy); }
        if (n.y > H) { n.y = H; n.vy = -Math.abs(n.vy); }
      }

      // ── gradient edges ────────────────────────────────────────────────────
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (d < DIST) {
            const proximity  = 1 - d / DIST;
            const isHubEdge  = nodes[i].isHub || nodes[j].isHub;
            const alpha      = proximity * (isHubEdge ? 0.62 : 0.42);
            const g = ctx.createLinearGradient(
              nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y
            );
            g.addColorStop(0, `rgba(${nodes[i].color}, ${alpha})`);
            g.addColorStop(0.5, `rgba(255, 255, 255, ${alpha * 0.18})`);
            g.addColorStop(1, `rgba(${nodes[j].color}, ${alpha})`);
            if (isHubEdge) {
              ctx.save();
              ctx.shadowBlur  = 6;
              ctx.shadowColor = `rgba(${nodes[i].isHub ? nodes[i].color : nodes[j].color}, 0.4)`;
            }
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = g;
            ctx.lineWidth   = isHubEdge ? 1.2 : 0.7;
            ctx.stroke();
            if (isHubEdge) ctx.restore();
          }
        }
      }

      // ── nodes ─────────────────────────────────────────────────────────────
      for (const n of nodes) {
        const a = 0.55 + Math.sin(n.φ) * 0.35;

        if (n.isHub) {
          // glow core
          ctx.save();
          ctx.shadowBlur  = 16;
          ctx.shadowColor = `rgba(${n.color}, 0.75)`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${n.color}, ${a})`;
          ctx.fill();
          ctx.restore();
          // pulsing outer ring
          const ring = n.r * (2.6 + Math.sin(n.φ * 0.6) * 0.5);
          ctx.beginPath();
          ctx.arc(n.x, n.y, ring, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${n.color}, ${a * 0.2})`;
          ctx.lineWidth = 0.9;
          ctx.stroke();
          // second faint ring
          ctx.beginPath();
          ctx.arc(n.x, n.y, ring * 1.5, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${n.color}, ${a * 0.07})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        } else {
          // regular node + soft halo
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${n.color}, ${a})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 2.2, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${n.color}, ${a * 0.18})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }

      // ── data-packet pulses ────────────────────────────────────────────────
      if (++tick % 42 === 0 && pulses.length < 10) spawnPulse();

      pulses = pulses.filter((p) => {
        p.t += p.dt;
        if (p.t > 1) return false;
        const x     = p.a.x + (p.b.x - p.a.x) * p.t;
        const y     = p.a.y + (p.b.y - p.a.y) * p.t;
        const alpha = Math.sin(p.t * Math.PI);

        // outer trail halo
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color}, ${alpha * 0.14})`;
        ctx.fill();

        // bright core
        ctx.save();
        ctx.shadowBlur  = 20;
        ctx.shadowColor = `rgba(${p.color}, ${alpha * 0.85})`;
        ctx.beginPath();
        ctx.arc(x, y, 3.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.92})`;
        ctx.fill();
        ctx.restore();

        return true;
      });

      raf = requestAnimationFrame(frame);
    }

    if (prefersReducedMotion) {
      // Render a single static frame — the network is still visible, but
      // nothing moves. The rAF loop never starts.
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      drawAmbient(W, H);
      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${n.color}, 0.6)`;
        ctx.fill();
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (d < DIST) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(${nodes[i].color}, ${(1 - d / DIST) * 0.35})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }
    } else {
      frame();
    }

    // Stop burning frames while the tab is in the background.
    function onVisibility() {
      if (prefersReducedMotion) return;
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(frame);
      }
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      document.removeEventListener("visibilitychange", onVisibility);
      host.removeEventListener("pointermove", onPointerMove);
      host.removeEventListener("pointerleave", onPointerLeave);
    };
  }, []);

  return <canvas ref={ref} className="neural-canvas" aria-hidden="true" />;
}
